import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
interface PurchaseCombo {
  label: string;
  description: string;
  priceText: string;
  thumbnailUrl: string;
  weight: number;
}
const PURCHASE_COMBOS: PurchaseCombo[] = [{
  label: 'Cuando Todo Recae Sobre Ti',
  description: 'Libro + 5 Bonos',
  priceText: '$14.00 USD',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/ofULURPzh8Qh9mHXzYVhsR/Mockup_Cuidado_de_Padres_Envejecidos.png',
  weight: 50
}, {
  label: 'Cuando Todo Recae Sobre Ti',
  description: 'Libro + 5 Bonos + Kit de Emergencia',
  priceText: '$20.00 USD',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/ofULURPzh8Qh9mHXzYVhsR/Mockup_Cuidado_de_Padres_Envejecidos.png',
  weight: 20
}, {
  label: 'Cuando Todo Recae Sobre Ti',
  description: 'Libro + 5 Bonos + Manual de Conversaciones',
  priceText: '$20.00 USD',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/ofULURPzh8Qh9mHXzYVhsR/Mockup_Cuidado_de_Padres_Envejecidos.png',
  weight: 15
}, {
  label: 'Cuando Todo Recae Sobre Ti',
  description: 'Libro + 5 Bonos + 2 Order Bumps',
  priceText: '$26.00 USD',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/ofULURPzh8Qh9mHXzYVhsR/Mockup_Cuidado_de_Padres_Envejecidos.png',
  weight: 15
}];
const NAMES = ['María', 'Carlos', 'Ana', 'José', 'Laura', 'Diego', 'Carmen', 'Miguel', 'Sofía', 'Andrés', 'Valentina', 'Ricardo', 'Gabriela', 'Fernando', 'Isabel', 'Roberto', 'Patricia', 'Alejandro', 'Rosa', 'Daniel', 'Lucía', 'Jorge', 'Elena', 'Francisco', 'Claudia', 'Raúl', 'Teresa', 'Eduardo', 'Mónica', 'Arturo', 'Sandra', 'Héctor', 'Adriana', 'Óscar', 'Verónica'];
const CITIES = ['Ciudad de México', 'Guadalajara', 'Monterrey', 'Puebla', 'Tijuana', 'León', 'Mérida', 'Cancún', 'Querétaro', 'San Luis Potosí', 'Aguascalientes', 'Toluca', 'Chihuahua', 'Morelia', 'Veracruz', 'Hermosillo', 'Saltillo', 'Villahermosa', 'Oaxaca', 'Tuxtla Gutiérrez', 'Culiacán', 'Mazatlán', 'Playa del Carmen', 'Cuernavaca', 'Pachuca', 'Tampico', 'Durango', 'Campeche', 'Zacatecas'];
interface Notification {
  id: string;
  name: string;
  city: string;
  combo: PurchaseCombo;
}
function pickWeightedCombo(): PurchaseCombo {
  const totalWeight = PURCHASE_COMBOS.reduce((sum, c) => sum + c.weight, 0);
  let rand = Math.random() * totalWeight;
  for (const combo of PURCHASE_COMBOS) {
    rand -= combo.weight;
    if (rand <= 0) return combo;
  }
  return PURCHASE_COMBOS[0];
}
export function RecentPurchaseToast() {
  const [notification, setNotification] = useState<Notification | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const nameHistory = useRef<string[]>([]);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const getRandomName = useCallback(() => {
    let available = NAMES.filter(n => !nameHistory.current.includes(n));
    if (available.length === 0) available = NAMES;
    const name = available[Math.floor(Math.random() * available.length)];
    nameHistory.current.push(name);
    if (nameHistory.current.length > 10) nameHistory.current.shift();
    return name;
  }, []);
  const getRandomCity = useCallback(() => CITIES[Math.floor(Math.random() * CITIES.length)], []);
  const showNext = useCallback(() => {
    const data: Notification = {
      id: Math.random().toString(36).slice(2, 9),
      name: getRandomName(),
      city: getRandomCity(),
      combo: pickWeightedCombo()
    };
    setNotification(data);
    setIsVisible(true);
    // Hide after 4s
    timeoutRef.current = setTimeout(() => {
      setIsVisible(false);
      // Next notification in 8-15s
      const delay = Math.floor(Math.random() * 7000) + 8000;
      timeoutRef.current = setTimeout(showNext, delay);
    }, 4000);
  }, [getRandomName, getRandomCity]);
  useEffect(() => {
    // First notification after 3s
    timeoutRef.current = setTimeout(showNext, 3000);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [showNext]);
  return <AnimatePresence data-id="element-387">
      {isVisible && notification && <motion.div key={notification.id} initial={prefersReducedMotion ? {
      opacity: 0
    } : {
      opacity: 0,
      y: 60
    }} animate={prefersReducedMotion ? {
      opacity: 1
    } : {
      opacity: 1,
      y: 0
    }} exit={prefersReducedMotion ? {
      opacity: 0
    } : {
      opacity: 0,
      y: 30
    }} transition={{
      duration: 0.45,
      ease: 'easeOut'
    }} className="fixed bottom-5 left-4 z-40 max-w-sm pointer-events-none" data-id="element-388">
          <div className="bg-white rounded-xl shadow-xl border border-navy/8 py-3 px-4 flex items-center gap-3" data-id="element-389">
            <img src={notification.combo.thumbnailUrl} alt={notification.combo.label} className="w-11 h-14 object-cover rounded-lg shadow-sm shrink-0 bg-cream" data-id="element-390" />
            <div className="flex-1 min-w-0" data-id="element-391">
              <p className="text-[13px] text-navy/75 leading-snug" data-id="element-392">
                <span className="font-semibold text-navy" data-id="element-393">
                  {notification.name}
                </span>{' '}
                en <span className="font-medium" data-id="element-394">{notification.city}</span>{' '}
                acaba de comprar
              </p>
              <p className="text-[13px] font-bold text-terracotta mt-0.5 leading-snug" data-id="element-395">
                {notification.combo.label}
              </p>
              <p className="text-[11px] text-navy/50 leading-snug" data-id="element-396">
                {notification.combo.description}
                {' — '}
                <span className="text-navy font-bold" data-id="element-397">
                  {notification.combo.priceText}
                </span>
              </p>
            </div>
            <div className="shrink-0 w-2 h-2 rounded-full bg-green-400 animate-pulse" data-id="element-398" />
          </div>
        </motion.div>}
    </AnimatePresence>;
}